import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Copy, Share2, Users, Trophy, Gift, Sparkles, Eye, Crown, Star, Zap, Palette, Rocket, Infinity as InfinityIcon,
  MessageCircle, Mail, Facebook, Twitter, Link as LinkIcon, TrendingUp, Loader2, Lock, Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TIER_ICONS = {
  star: Star, eye: Eye, gift: Gift, crown: Crown, trophy: Trophy,
  sparkles: Sparkles, rocket: Rocket, palette: Palette, infinity: InfinityIcon
};

const ViralReferralWidget = ({ compact = false }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);

  const fetchStats = useCallback(async () => {
    try {
      const [meRes, lbRes] = await Promise.all([
        axios.get(`${API_URL}/api/viral/me`),
        axios.get(`${API_URL}/api/viral/leaderboard?limit=5`),
      ]);
      setStats(meRes.data);
      setLeaderboard(lbRes.data.leaderboard || []);
    } catch (e) {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 p-6 flex items-center justify-center" data-testid="viral-widget-loading">
        <Loader2 className="h-5 w-5 animate-spin text-[#E07A3A]" />
      </div>
    );
  }

  if (!stats) return null;

  const copyLink = () => {
    navigator.clipboard.writeText(stats.share_link);
    toast.success('Referral link copied!');
  };

  const copyCode = () => {
    navigator.clipboard.writeText(stats.code);
    toast.success(`Code ${stats.code} copied!`);
  };

  const shareText = encodeURIComponent(
    `Join me on Shopperden — 3D virtual property tours, AI valuations, zero agent fees. Use my code ${stats.code} when you sign up:`
  );
  const shareUrl = encodeURIComponent(stats.share_link);  const shareLinks = {
    whatsapp: `https://wa.me/?text=${shareText}%20${shareUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}&quote=${shareText}`,
    twitter: `https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`,
    messenger: `https://www.facebook.com/dialog/send?link=${shareUrl}&app_id=0&redirect_uri=${shareUrl}`,
    email: `mailto:?subject=Join%20me%20on%20Shopperden&body=${shareText}%20${shareUrl}`,
    sms: `sms:?&body=${shareText}%20${shareUrl}`,
  };

  const pct = stats.next_reward
    ? Math.min(100, Math.round(((stats.referrals_count || 0) / stats.next_reward.count) * 100))
    : 100;

  return (
    <div className="space-y-6" data-testid="viral-referral-widget">
      {/* Hero card — referral code + share */}
      <div className="bg-gradient-to-br from-[#0F172A] to-[#1e293b] text-white p-6 sm:p-8 border border-[#E07A3A]/30">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex-1">
            <div className="flex items-center gap-2 text-[#E07A3A] text-xs tracking-[0.2em] uppercase font-bold mb-2">
              <Sparkles className="h-3.5 w-3.5" />
              Level Up — Invite Friends
            </div>
            <h3 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-semibold mb-2">
              Your Personal<br /><span className="text-[#E07A3A] italic">Shopperden Code</span>
            </h3>
            <p className="text-white/50 text-sm">Share your link. Unlock exclusive features, titles, and profile perks as you climb the ladder.</p>
          </div>
          <div className="hidden sm:flex items-center justify-center w-14 h-14 border border-[#E07A3A]/40">
            <Trophy className="h-7 w-7 text-[#E07A3A]" />
          </div>
        </div>

        {/* Code + Copy */}
        <div className="grid sm:grid-cols-2 gap-3 mb-5">
          <button
            onClick={copyCode}
            className="bg-white/5 hover:bg-white/10 border border-white/10 px-4 py-3.5 text-left transition-colors"
            data-testid="viral-copy-code-btn"
          >
            <p className="text-[10px] text-white/40 tracking-[0.2em] uppercase mb-1">Your Code</p>
            <p className="font-mono text-xl text-[#E07A3A] font-bold tracking-wider truncate">{stats.code}</p>
          </button>
          <button
            onClick={copyLink}
            className="bg-[#E07A3A] hover:bg-[#C4652E] px-4 py-3.5 text-left transition-colors group"
            data-testid="viral-copy-link-btn"
          >
            <p className="text-[10px] text-white/70 tracking-[0.2em] uppercase mb-1 flex items-center gap-1.5">
              <LinkIcon className="h-3 w-3" /> Your Share Link — Click to Copy
            </p>
            <p className="text-sm text-white font-semibold truncate flex items-center gap-2">
              <span className="truncate">{stats.share_link}</span>
              <Copy className="h-3.5 w-3.5 flex-shrink-0 opacity-60 group-hover:opacity-100" />
            </p>
          </button>
        </div>

        {/* Social share grid */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          <a href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer"
             className="flex flex-col items-center gap-1 bg-white/5 hover:bg-[#25D366] text-white py-3 px-2 transition-colors group"
             data-testid="viral-share-whatsapp">
            <MessageCircle className="h-4 w-4" />
            <span className="text-[10px] tracking-wider uppercase font-semibold">WhatsApp</span>
          </a>
          <a href={shareLinks.facebook} target="_blank" rel="noopener noreferrer"
             className="flex flex-col items-center gap-1 bg-white/5 hover:bg-[#1877F2] text-white py-3 px-2 transition-colors"
             data-testid="viral-share-facebook">
            <Facebook className="h-4 w-4" />
            <span className="text-[10px] tracking-wider uppercase font-semibold">Facebook</span>
          </a>
          <a href={shareLinks.twitter} target="_blank" rel="noopener noreferrer"
             className="flex flex-col items-center gap-1 bg-white/5 hover:bg-black text-white py-3 px-2 transition-colors"
             data-testid="viral-share-twitter">
            <Twitter className="h-4 w-4" />
            <span className="text-[10px] tracking-wider uppercase font-semibold">Twitter</span>
          </a>
          <a href={shareLinks.messenger} target="_blank" rel="noopener noreferrer"
             className="flex flex-col items-center gap-1 bg-white/5 hover:bg-[#A334FA] text-white py-3 px-2 transition-colors"
             data-testid="viral-share-messenger">
            <MessageCircle className="h-4 w-4" />
            <span className="text-[10px] tracking-wider uppercase font-semibold">Messenger</span>
          </a>
          <a href={shareLinks.email}
             className="flex flex-col items-center gap-1 bg-white/5 hover:bg-[#E07A3A] text-white py-3 px-2 transition-colors"
             data-testid="viral-share-email">
            <Mail className="h-4 w-4" />
            <span className="text-[10px] tracking-wider uppercase font-semibold">Email</span>
          </a>
          <a href={shareLinks.sms}
             className="flex flex-col items-center gap-1 bg-white/5 hover:bg-slate-600 text-white py-3 px-2 transition-colors"
             data-testid="viral-share-sms">
            <Share2 className="h-4 w-4" />
            <span className="text-[10px] tracking-wider uppercase font-semibold">SMS</span>
          </a>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatBox icon={<Users className="h-4 w-4" />} label="Invites" value={stats.referrals_count} testid="stat-invites" />
        <StatBox icon={<TrendingUp className="h-4 w-4" />} label="Qualified" value={stats.successful_referrals_count} testid="stat-qualified" />
        <StatBox icon={<Eye className="h-4 w-4" />} label="Link Clicks" value={stats.clicks} testid="stat-clicks" />
        <StatBox icon={<Zap className="h-4 w-4" />} label={`Lvl ${stats.level || 1} · XP`} value={stats.xp || 0} testid="stat-xp" highlight />
      </div>

      {/* Level progress bar */}
      <div className="bg-white border border-slate-200 p-5" data-testid="viral-level-bar">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#E07A3A] text-white flex items-center justify-center font-bold text-sm">
              {stats.level || 1}
            </div>
            <div>
              <p className="text-[10px] text-slate-400 tracking-[0.2em] uppercase font-bold">Ambassador Level</p>
              <p className="text-sm text-[#0F172A] font-semibold">{stats.xp || 0} XP earned</p>
            </div>
          </div>
          <p className="text-xs text-slate-500">
            {stats.xp_to_next_level > 0 ? `${stats.xp_to_next_level} XP to Lvl ${(stats.level || 1) + 1}` : 'MAX LEVEL'}
          </p>
        </div>
        <div className="w-full bg-slate-100 h-2 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#E07A3A] to-[#f59e0b] transition-all duration-500" style={{ width: `${stats.level_progress_pct || 0}%` }} />
        </div>
      </div>

      {/* Progress to next reward */}
      {stats.next_reward && (
        <div className="bg-white border border-slate-200 p-5" data-testid="viral-next-reward">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[10px] text-slate-400 tracking-[0.2em] uppercase font-bold mb-1">Next Unlock</p>
              <p className="font-['Playfair_Display'] text-lg text-[#0F172A] font-semibold">{stats.next_reward.label}</p>
              {stats.next_reward.desc && (
                <p className="text-xs text-slate-500 mt-1">{stats.next_reward.desc}</p>
              )}
            </div>
            <div className="text-right flex-shrink-0 ml-4">
              <p className="text-2xl font-bold text-[#E07A3A]">{stats.next_reward.remaining}</p>
              <p className="text-[10px] text-slate-400 tracking-wider uppercase">to go</p>
            </div>
          </div>
          <div className="w-full bg-slate-100 h-2 overflow-hidden">
            <div className="h-full bg-[#E07A3A] transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-xs text-slate-500 mt-2">
            {stats.referrals_count} of {stats.next_reward.count} invites · +{stats.next_reward.xp || 0} XP when unlocked
          </p>
        </div>
      )}

      {/* Reward tiers */}
      {!compact && (
        <div className="bg-white border border-slate-200 p-5" data-testid="viral-reward-tiers">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[10px] text-slate-400 tracking-[0.2em] uppercase font-bold">Unlock Ladder</p>
            <p className="text-[10px] text-slate-400 tracking-wider uppercase">{stats.earned_rewards.length} / {stats.all_tiers.length} unlocked</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            {stats.all_tiers.map(tier => {
              const Icon = TIER_ICONS[tier.icon] || Gift;
              const earned = stats.referrals_count >= tier.count;
              return (
                <div
                  key={tier.key}
                  className={`relative border p-3 text-center transition-all ${
                    earned ? 'bg-[#FEF3EC] border-[#E07A3A] shadow-sm' : 'bg-slate-50 border-slate-200 opacity-80'
                  }`}
                  data-testid={`tier-${tier.key}`}
                  title={tier.desc}
                >
                  {!earned && (
                    <Lock className="absolute top-1.5 right-1.5 h-3 w-3 text-slate-300" />
                  )}
                  {earned && (
                    <Check className="absolute top-1.5 right-1.5 h-3 w-3 text-[#E07A3A]" />
                  )}
                  <Icon className={`h-6 w-6 mx-auto mb-1.5 ${earned ? 'text-[#E07A3A]' : 'text-slate-300'}`} />
                  <p className={`text-xs font-bold mb-0.5 ${earned ? 'text-[#0F172A]' : 'text-slate-400'}`}>
                    {tier.count} invite{tier.count > 1 ? 's' : ''}
                  </p>
                  <p className={`text-[10px] leading-tight ${earned ? 'text-[#0F172A]' : 'text-slate-400'}`}>
                    {tier.label}
                  </p>
                  {tier.xp && (
                    <p className={`text-[9px] mt-1 tracking-wider uppercase font-semibold ${earned ? 'text-[#E07A3A]' : 'text-slate-300'}`}>
                      +{tier.xp} XP
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          {stats.unlocked_features && stats.unlocked_features.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <p className="text-[10px] text-slate-400 tracking-[0.2em] uppercase font-bold mb-2">Active Perks</p>
              <div className="flex flex-wrap gap-2" data-testid="unlocked-features">
                {stats.unlocked_features.map(f => (
                  <span key={f} className="text-[11px] bg-[#FEF3EC] text-[#E07A3A] px-2.5 py-1 font-semibold tracking-wide">
                    {f.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recent referrals + Leaderboard */}
      {!compact && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 p-5">
            <p className="text-[10px] text-slate-400 tracking-[0.2em] uppercase font-bold mb-3">Your Recent Invites</p>
            {stats.recent_referrals.length === 0 ? (
              <p className="text-xs text-slate-400 italic">No invites yet — share your link to start earning rewards.</p>
            ) : (
              <div className="space-y-2" data-testid="viral-recent-list">
                {stats.recent_referrals.map((r, idx) => (
                  <div key={`${r.first_name}-${idx}`} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div>
                      <p className="text-sm text-[#0F172A] font-medium">{r.first_name}</p>
                      <p className="text-[10px] text-slate-400 capitalize tracking-wider">{r.role}</p>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 tracking-wider uppercase font-semibold ${
                      r.qualified ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {r.qualified ? 'Qualified' : 'Signed up'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200 p-5">
            <p className="text-[10px] text-slate-400 tracking-[0.2em] uppercase font-bold mb-3">Top Referrers (Global)</p>
            {leaderboard.length === 0 ? (
              <p className="text-xs text-slate-400 italic">Be the first to invite friends and claim the top spot.</p>
            ) : (
              <div className="space-y-2" data-testid="viral-leaderboard">
                {leaderboard.map(u => (
                  <div key={u.rank} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-3">
                      <span className={`font-['Playfair_Display'] text-lg font-bold ${
                        u.rank === 1 ? 'text-[#E07A3A]' : 'text-slate-400'
                      }`}>#{u.rank}</span>
                      <div>
                        <p className="text-sm text-[#0F172A] font-medium">{u.first_name}</p>
                        <p className="text-[10px] text-slate-400 tracking-wider uppercase">{u.country} · {u.role}</p>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-[#E07A3A]">{u.referrals_count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const StatBox = ({ icon, label, value, testid }) => (
  <div className="bg-white border border-slate-200 p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-slate-400 text-[10px] tracking-[0.2em] uppercase font-bold mb-1">
      {icon}
      {label}
    </div>
    <p className="text-2xl font-['Playfair_Display'] font-semibold text-[#0F172A]">{value}</p>
  </div>
);

export default ViralReferralWidget;
